import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";

import { faColumns3, faPlus, faSliders, faUpload } from "@fortawesome/pro-regular-svg-icons";

import { Table, TableColumn } from "@/components/Table/Table";
import { TItem } from "@/components/SortableList/SortableList";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Order, SortParams } from "@/types/types";
import { PermissionRoles } from "@/enum";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { useSnackBar } from "@/utils/useSnackbar";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useLazyGetBlacklistedContactsQuery,
	useContactRemoveFromBlacklistMutation,
	useContactSnoozeProspectsMutation,
	useContactUnsnoozeProspectsMutation,
} from "@/pages/Private/redux/contact/contact.api";

import { applyPersistedColumnConfig } from "@/components/Table/applyPersistedColumnConfig";

import { canManageGlobalBlacklist } from "../utils/canManageGlobalBlacklist";
import { showKeptUnsubscribesToast } from "../utils/keptUnsubscribedToast";

import { ContactSelectType } from "../../Contacts/schema/contacts";

import { ContactsBlacklistFilters } from "../../Contacts/constants/contacts-filter";
import { useContactsUrlState } from "../../Contacts/hooks/useContactsUrlState";

import { BlacklistTableColumns } from "./TableColumnConfigs/BlacklistTableColumns";
import { UnflagCompetitorModal } from "./UnflagCompetitorModal";
import { useCompetitorUnflag } from "../hooks/useCompetitorUnflag";
import { useBlacklistSelection } from "../utils/useBlacklistSelection";
import { BlacklistTableModals } from "./BlacklistTableModals";
import { ContactsColumnModal } from "../../Contacts/components/ContactsColumnModal";
import { ContactsFilterModal } from "../../Contacts/components/ContactsFilterModal";
import { CreateBlacklistModal } from "./CreateBlacklistModal";
import { UploadBlacklistModal } from "./UploadBlacklistModal";

export const BlacklistTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`contacts.${key}`), [t]);

	const { user } = useAppSelector(authSelector);
	// Global entries block for every workspace: staff only (PREC-4347).
	const userCanManageGlobal = user ? canManageGlobalBlacklist(user) : false;
	const { hasPermissions } = useHasPermissions();
	const canUpload = hasPermissions([PermissionRoles.UPLOAD_FILES, PermissionRoles.CSV_ENRICHMENTS]);

	const {
		page,
		setPage,
		limit,
		setLimit,
		sortingInbox,
		setSortingInbox,
		filter,
		setFilter,
		applyFilters,
		searchValue,
		setSearchValue,
		query,
	} = useContactsUrlState({
		prefix: "blacklist",
		defaultSortBy: "addedToBlacklistAt",
		defaultSortOrder: Order.DESC,
		withSearch: true,
	}) as ReturnType<typeof useContactsUrlState> & {
		searchValue: string;
		setSearchValue: (v: string) => void;
		query: string;
		isTyping: boolean;
	};

	const [showFilterModal, setShowFilterModal] = useState(false);
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showAll, setShowAll] = useState(false);
	// Bumped for PREC-3594: the column set and its order changed, and a saved v1 layout would
	// have kept the old order while silently hiding every new column.
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		"contacts-columns-blacklist-v2",
		{
			defaultValue: [],
		}
	);

	const [showRemoveFromBlacklistModal, setShowRemoveFromBlacklistModal] = useState(false);
	const [removeOrganization, setRemoveOrganization] = useState(false);
	const [showSnoozeModal, setShowSnoozeModal] = useState(false);
	const [showUnsnoozeModal, setShowUnsnoozeModal] = useState(false);
	const [snoozedUntil, setSnoozedUntil] = useState<Date>(
		new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
	);
	const [prepSelectType, setPrepSelectType] = useState<ContactSelectType>(ContactSelectType.ALL);

	const { renderSnackbar, handleCopyClick } = useCopyToClipboard();
	const { renderMessageSnackbar, handleOpenSnackbar } = useSnackBar();

	const [removeFromBlacklist, { isLoading: isRemoveFromBlacklistLoading }] =
		useContactRemoveFromBlacklistMutation();
	const [snoozeProspects, { isLoading: isSnoozeLoading }] = useContactSnoozeProspectsMutation();
	const [unsnoozeProspects, { isLoading: isUnsnoozeLoading }] =
		useContactUnsnoozeProspectsMutation();

	const [triggerGetBlacklisted, { data: response, isFetching }] =
		useLazyGetBlacklistedContactsQuery();
	const lastQueryPromise = useRef<ReturnType<typeof triggerGetBlacklisted> | null>(null);

	useEffect(() => {
		lastQueryPromise.current?.abort();
		lastQueryPromise.current = triggerGetBlacklisted({
			page,
			limit,
			searchValue: query || undefined,
			sortBy: sortingInbox.sortBy,
			sortOrder: sortingInbox.sortOrder,
			...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
		});
	}, [
		page,
		limit,
		query,
		sortingInbox.sortBy,
		sortingInbox.sortOrder,
		filter,
		triggerGetBlacklisted,
	]);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
	};

	const handleSorting = (sortParams: SortParams) => {
		setSortingInbox(sortParams);
	};

	const data = response?.data;
	const meta = response?.meta;

	const {
		selectedItems,
		setSelectedItems,
		selectAll,
		selectType,
		selectedCount,
		hasSnoozedItems,
		canSnooze,
		getSelectionPayload,
		clearSelection,
		toggleSelectAll,
		selectItem,
		applySelectType,
	} = useBlacklistSelection({ data, filter, query, itemCount: meta?.itemCount });

	const handleRemoveFromBlacklist = useCallback(async () => {
		try {
			const payload = { ...getSelectionPayload(), removeOrganization };
			const result = await removeFromBlacklist(payload).unwrap();

			// An unsubscribe stays on the blacklist (PREC-2548): say whom the removal left there.
			showKeptUnsubscribesToast(result?.skipped, t);
			clearSelection();
			setShowRemoveFromBlacklistModal(false);
			setRemoveOrganization(false);
		} catch (error) {
			console.error(error);
			handleOpenSnackbar(t("basics.error"));
		}
	}, [
		removeFromBlacklist,
		getSelectionPayload,
		removeOrganization,
		clearSelection,
		handleOpenSnackbar,
		t,
	]);

	const handleSnooze = useCallback(async () => {
		try {
			await snoozeProspects({
				...getSelectionPayload(),
				snoozedUntil: snoozedUntil.toISOString(),
			}).unwrap();
			clearSelection();
			setShowSnoozeModal(false);
		} catch (error) {
			console.error(error);
			handleOpenSnackbar(t("basics.error"));
		}
	}, [snoozeProspects, getSelectionPayload, snoozedUntil, clearSelection, handleOpenSnackbar, t]);

	const handleUnsnooze = useCallback(async () => {
		try {
			await unsnoozeProspects(getSelectionPayload()).unwrap();
			clearSelection();
			setShowUnsnoozeModal(false);
		} catch (error) {
			console.error(error);
			handleOpenSnackbar(t("basics.error"));
		}
	}, [unsnoozeProspects, getSelectionPayload, clearSelection, handleOpenSnackbar, t]);

	const handleConfirmSelectType = useCallback(() => {
		applySelectType(prepSelectType);
		setShowAll(false);
		setPrepSelectType(ContactSelectType.ALL);
	}, [applySelectType, prepSelectType]);

	/** Row-level "Remove from blacklist": select just that row and open the usual confirmation. */
	const handleRemoveRow = useCallback(
		(rowId: number) => {
			setSelectedItems([rowId]);
			setRemoveOrganization(false);
			setShowRemoveFromBlacklistModal(true);
		},
		[setSelectedItems]
	);

	const unflag = useCompetitorUnflag({
		clearSelection,
		getSelectionPayload,
		openSnackbar: handleOpenSnackbar,
	});

	const buildColumns = useCallback(
		() =>
			BlacklistTableColumns({
				handleCopyClick,
				ts,
				sortingInbox,
				setShowAll,
				toggleSelectAll,
				selectAll,
				selectedItems,
				handleSelectItem: selectItem,
				onUnflagCompetitor: unflag.askForRow,
				onRemoveFromBlacklist: handleRemoveRow,
			}),
		[
			handleCopyClick,
			ts,
			sortingInbox,
			setShowAll,
			toggleSelectAll,
			selectAll,
			selectedItems,
			selectItem,
			unflag.askForRow,
			handleRemoveRow,
		]
	);

	// Same merge the List and Contacts tables use for a saved column layout.
	const columns = useMemo(
		() => applyPersistedColumnConfig(buildColumns(), tableConfig),
		[buildColumns, tableConfig]
	);

	const resetTableConfig = (force = false) => {
		const sourceCols = force ? buildColumns() : columns;

		const newConfig: TItem[] = sourceCols.map((c: TableColumn, index: number) => ({
			uid: c.uid ?? "",
			id: index + 1,
			show: typeof c.show !== "undefined" ? c.show : true,
			content: c.title,
			locked: !!c.locked || false,
			allowExclude: typeof c.allowExclude !== "undefined" ? c.allowExclude : true,
			custom: false,
			moveable: c.moveable,
			hideable: c.hideable,
		}));

		if (tableConfig.length === 0 || force) {
			setTableConfig(newConfig);
		}
	};

	useMemo(() => {
		if (columns.length > 0 && tableConfig.length === 0) {
			resetTableConfig();
		}
	}, [columns]);

	return (
		<div className="w-full">
			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						name="search"
						placeholder={ts("searchPlaceholder")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				<div className="ml-auto flex flex-row items-center gap-2">
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faColumns3} />}
							size={ButtonSize.S}
							title={ts("selectColumns")}
							onClick={() => setShowSelectColumns(true)}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faSliders} />}
							size={ButtonSize.S}
							title={`${ts("filter")}${filter && Object.keys(filter).length > 0 ? ` (${Object.keys(filter).length})` : ""}`}
							onClick={() => setShowFilterModal(true)}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faPlus} />}
							size={ButtonSize.S}
							title={ts("blacklist.addEntry")}
							onClick={() => setShowCreateModal(true)}
						/>
					</div>
					{canUpload && (
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="mr-2" icon={faUpload} />}
								size={ButtonSize.S}
								title={ts("blacklist.upload")}
								onClick={() => setShowUploadModal(true)}
							/>
						</div>
					)}
				</div>
			</div>

			<Table
				columns={columns}
				data={response?.data || []}
				filter={filter}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleFetchPage={setPage}
				handleSorting={handleSorting}
				isLoading={isFetching}
				limit={limit}
				noDataCta={
					canUpload
						? {
								title: ts("blacklist.importBlacklist"),
								action: () => setShowUploadModal(true),
							}
						: undefined
				}
				noDataText={ts("blacklist.empty")}
				overallCount={meta?.itemCount}
				page={page}
				removeFromBlacklist={
					selectedItems.length > 0
						? {
								removeFromBlacklist: () => setShowRemoveFromBlacklistModal(true),
								removeFromBlacklistIsLoading: isRemoveFromBlacklistLoading,
							}
						: undefined
				}
				clearSelection={() => setSelectedItems([])}
				selectedCount={selectAll ? meta?.itemCount : selectedItems.length}
				showFooterFunctions={selectedItems.length > 0}
				snooze={
					selectedItems.length > 0 && canSnooze
						? {
								snooze: () => setShowSnoozeModal(true),
								snoozeIsLoading: isSnoozeLoading,
							}
						: undefined
				}
				sticky
				tableConfig={tableConfig}
				totalPages={meta?.pageCount}
				unsnooze={
					selectedItems.length > 0 && canSnooze && hasSnoozedItems
						? {
								unsnooze: () => setShowUnsnoozeModal(true),
								unsnoozeIsLoading: isUnsnoozeLoading,
							}
						: undefined
				}
			/>

			{showSelectColumns && (
				<ContactsColumnModal
					handleResetToDefault={() => {
						setTableConfig([]);
						setTimeout(() => resetTableConfig(true), 100);
					}}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={tableConfig}
				/>
			)}

			{showFilterModal && (
				<ContactsFilterModal
					applyFilters={applyFilters}
					filter={filter}
					filterDataType={ContactsBlacklistFilters}
					setFilter={setFilter}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
				/>
			)}

			{showCreateModal && (
				<CreateBlacklistModal
					isAdmin={userCanManageGlobal}
					isGlobal={false}
					isOpen={showCreateModal}
					setIsOpen={setShowCreateModal}
				/>
			)}

			{showUploadModal && (
				<UploadBlacklistModal
					canImportGlobal={userCanManageGlobal}
					isGlobal={false}
					isOpen={showUploadModal}
					setIsOpen={setShowUploadModal}
				/>
			)}

			<BlacklistTableModals
				isRemoving={isRemoveFromBlacklistLoading}
				isSnoozing={isSnoozeLoading}
				isUnsnoozing={isUnsnoozeLoading}
				itemCount={meta?.itemCount}
				limit={limit}
				prepSelectType={prepSelectType}
				removeOrganization={removeOrganization}
				selectedCount={selectedCount}
				setPrepSelectType={setPrepSelectType}
				setRemoveOrganization={setRemoveOrganization}
				setShowAll={setShowAll}
				setShowRemoveModal={setShowRemoveFromBlacklistModal}
				setShowSnoozeModal={setShowSnoozeModal}
				setShowUnsnoozeModal={setShowUnsnoozeModal}
				setSnoozedUntil={setSnoozedUntil}
				showAll={showAll}
				showRemoveModal={showRemoveFromBlacklistModal}
				showSnoozeModal={showSnoozeModal}
				showUnsnoozeModal={showUnsnoozeModal}
				snoozedUntil={snoozedUntil}
				ts={ts}
				onConfirmSelectType={handleConfirmSelectType}
				onRemove={handleRemoveFromBlacklist}
				onSnooze={handleSnooze}
				onUnsnooze={handleUnsnooze}
			/>

			{unflag.pending && (
				<UnflagCompetitorModal
					companyName={unflag.pending.companyName}
					count={unflag.pending.count}
					isLoading={unflag.isUnflagging}
					onClose={unflag.cancel}
					onConfirm={unflag.confirm}
				/>
			)}

			{renderSnackbar()}
			{renderMessageSnackbar({ onClick: () => unflag.undo(unflag.undoableIds) })}
		</div>
	);
};
